import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Global toast state
let toastManager = {
  toasts: [],
  listeners: [],
  addToast: function(toast) {
    this.toasts = [...this.toasts, { ...toast, id: Date.now() }];
    this.notifyListeners();
  },
  removeToast: function(id) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notifyListeners();
  },
  clearAll: function() {
    this.toasts = [];
    this.notifyListeners();
  },
  subscribe: function(listener) {
    this.listeners = [...this.listeners, listener];
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  },
  notifyListeners: function() {
    this.listeners.forEach(listener => listener(this.toasts));
  }
};

// Toast types
export const ToastType = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning'
};

// Toast functions for use throughout the app
export const toast = {
  success: (message) => toastManager.addToast({ message, type: ToastType.SUCCESS }),
  error: (message) => toastManager.addToast({ message, type: ToastType.ERROR }),
  info: (message) => toastManager.addToast({ message, type: ToastType.INFO }),
  warning: (message) => toastManager.addToast({ message, type: ToastType.WARNING }),
  clear: () => toastManager.clearAll()
};

// Toast container component
export function ToastContainer({ position = 'top-right', autoClose = 3000 }) {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = toastManager.subscribe(setToasts);
    return unsubscribe;
  }, []);

  const getPositionStyle = () => {
    switch (position) {
      case 'top-left':
        return { top: '20px', left: '20px' };
      case 'top-center':
        return { top: '20px', left: '50%', transform: 'translateX(-50%)' };
      case 'top-right':
        return { top: '20px', right: '20px' };
      case 'bottom-left':
        return { bottom: '20px', left: '20px' };
      case 'bottom-center':
        return { bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-right':
        return { bottom: '20px', right: '20px' };
      default:
        return { top: '20px', right: '20px' };
    }
  };

  return (
    <div 
      style={{ 
        position: 'fixed',
        zIndex: 9999,
        ...getPositionStyle(),
        display: 'flex',
        flexDirection: 'column',
        alignItems: position.includes('center') ? 'center' : 'flex-start',
        gap: '10px'
      }}
    >
      {toasts.map(toast => (
        <Toast 
          key={toast.id} 
          id={toast.id}
          type={toast.type} 
          message={toast.message} 
          autoClose={autoClose} 
        />
      ))}
    </div>
  );
}

// Individual toast component
function Toast({ id, type, message, autoClose }) {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        toastManager.removeToast(id);
      }, autoClose);
      
      return () => clearTimeout(timer);
    }
  }, [id, autoClose]);

  const getToastStyles = () => {
    const baseStyles = {
      padding: '10px 20px',
      borderRadius: '4px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minWidth: '300px',
      maxWidth: '400px',
      animation: 'slideIn 0.3s ease-in-out forwards'
    };

    switch (type) {
      case ToastType.SUCCESS:
        return { ...baseStyles, backgroundColor: '#4caf50', color: 'white' };
      case ToastType.ERROR:
        return { ...baseStyles, backgroundColor: '#f44336', color: 'white' };
      case ToastType.INFO:
        return { ...baseStyles, backgroundColor: '#2196f3', color: 'white' };
      case ToastType.WARNING:
        return { ...baseStyles, backgroundColor: '#ff9800', color: 'white' };
      default:
        return { ...baseStyles, backgroundColor: '#333', color: 'white' };
    }
  };

  return (
    <div style={{...getToastStyles()}}>
      <div style={{ flexGrow: 1, margin: '0 10px' }}>{message}</div>
      <button 
        onClick={() => toastManager.removeToast(id)}
        style={{ 
          background: 'none', 
          border: 'none', 
          color: 'white', 
          fontSize: '16px',
          cursor: 'pointer',
          padding: '0 5px'
        }}
      >
        &times;
      </button>
    </div>
  );
}

Toast.propTypes = {
  id: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  autoClose: PropTypes.number
};

ToastContainer.propTypes = {
  position: PropTypes.oneOf([
    'top-left', 'top-center', 'top-right', 
    'bottom-left', 'bottom-center', 'bottom-right'
  ]),
  autoClose: PropTypes.number
};